import { Box, ListItem, ListItemButton, ListItemText } from '@mui/material';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { SearchRequest } from '../../api/CoreSearchApi';
import { useAuthContext } from '../../context/auth';
import SearchCardSkeleton from '../search/SearchCardSkeleton';
import {
	SEARCH___END_OF_RESULTS,
	SEARCH___ERROR,
	SEARCH___LOADING_MORE,
	SEARCH___LOAD_MORE,
	SEARCH___RESULTS_NO_RESULTS,
} from './constants';
import { usePaginatedCoreSearch } from './coreSearchQueries';
import CoreSearchResultListItem from './CoreSearchResultListItem';
import { SEARCH_BOX__SEARCH_RESULT_HEIGHT } from './styles';

interface Props {
	searchRequest: SearchRequest;
	afterListClick?: () => void;
}

export function CoreSearchResultList(props: Props) {
	const { searchRequest, afterListClick } = props;
	const { ref, inView } = useInView();
	const { auth } = useAuthContext();
	const { authorizationToken: authToken } = auth;

	const {
		data: searchResults,
		isLoading,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
		error,
	} = usePaginatedCoreSearch(authToken, searchRequest);

	React.useEffect(() => {
		if (inView) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView]);

	const allSearchResults = searchResults?.pages
		?.map((searchResponse) => searchResponse?.results)
		?.flat()
		?.filter((searchResult) => !!searchResult?.type);

	let bottomRowText = '';
	if (allSearchResults?.length === 0 && !!searchRequest?.searchString) {
		bottomRowText = SEARCH___RESULTS_NO_RESULTS;
	} else if (isFetchingNextPage) {
		bottomRowText = SEARCH___LOADING_MORE;
	} else if (hasNextPage) {
		bottomRowText = SEARCH___LOAD_MORE;
	} else if (!!searchRequest?.searchString) {
		bottomRowText = SEARCH___END_OF_RESULTS;
	}

	return (
		<Box sx={{ flex: 1, overflowY: 'auto' }}>
			{!isLoading &&
				allSearchResults?.map((searchResultResponse, index) => {
					if (!searchResultResponse) {
						return null;
					}
					return (
						<CoreSearchResultListItem
							key={`${searchResultResponse.policyLocator}-${searchResultResponse.type}${index}`}
							searchResult={searchResultResponse}
							index={index}
							afterListClick={afterListClick}
						/>
					);
				})}
			{isLoading &&
				!error &&
				Array(5)
					.fill(0)
					.map((_, index) => (
						<React.Fragment key={index}>
							<ListItem disableGutters disablePadding>
								<ListItemButton
									sx={{ height: SEARCH_BOX__SEARCH_RESULT_HEIGHT }}
									disabled
								>
									<SearchCardSkeleton />
								</ListItemButton>
							</ListItem>
						</React.Fragment>
					))}
			{!isLoading && !!error && (
				<>
					<ListItem disableGutters disablePadding>
						<ListItemText
							sx={{ p: 2, textAlign: 'center' }}
							primary={SEARCH___ERROR}
						/>
					</ListItem>
				</>
			)}
			{!isLoading && !error && !!bottomRowText && (
				<>
					<ListItem ref={ref} disableGutters disablePadding>
						<ListItemText
							sx={{ p: 2, textAlign: 'center' }}
							primary={bottomRowText}
						/>
					</ListItem>
				</>
			)}
		</Box>
	);
}
